/**
 * DrawingToolbar - Simplified toolbar for path drawing
 *
 * Only ONE tool: Draw Path (Polyline)
 * - Click to add points
 * - Click on first point to close (for land plots)
 * - Press Enter or click ✓ to finish (for open paths like routes)
 * - Press Escape to cancel
 */
import { useState } from 'react'
import clsx from 'clsx'
import {
  HiOutlineCheck,
  HiOutlineX,
} from 'react-icons/hi'
import { TbLine } from 'react-icons/tb'

export default function DrawingToolbar({
  isDrawing = false,
  pointCount = 0,
  canSnapClose = false,
  onStartDrawing,
  onFinish,
  onCancel,
  disabled = false,
}) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div className="absolute left-4 top-1/2 -translate-y-1/2 z-[1000] flex flex-col gap-2">
      {/* Draw Path Button */}
      <div className="relative">
        <button
          onClick={() => {
            if (isDrawing) {
              onCancel?.()
            } else {
              onStartDrawing?.()
            }
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={disabled}
          className={clsx(
            'w-10 h-10 flex items-center justify-center rounded-lg shadow-lg transition-all',
            isDrawing
              ? 'bg-primary-600 text-white ring-2 ring-primary-300'
              : 'bg-white text-gray-600 hover:bg-gray-50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          title="Vẽ đường (L)"
        >
          <TbLine className="w-5 h-5" />
        </button>

        {/* Tooltip */}
        {isHovered && !isDrawing && (
          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 whitespace-nowrap">
            <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg">
              Vẽ đường
              <span className="ml-2 text-gray-400">L</span>
            </div>
          </div>
        )}
      </div>

      {/* Drawing Controls - shown when drawing */}
      {isDrawing && (
        <div className="bg-white rounded-xl shadow-lg p-1.5 flex flex-col gap-1">
          {/* Finish button */}
          <button
            onClick={onFinish}
            disabled={pointCount < 2}
            className={clsx(
              'w-10 h-10 flex items-center justify-center rounded-lg transition-colors',
              pointCount >= 2
                ? 'bg-green-100 text-green-600 hover:bg-green-200'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            )}
            title={`Hoàn thành (Enter) - ${pointCount} điểm`}
          >
            <HiOutlineCheck className="w-5 h-5" />
          </button>

          {/* Cancel button */}
          <button
            onClick={onCancel}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
            title="Hủy (Esc)"
          >
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Drawing Hint */}
      {isDrawing && (
        <div
          className={clsx(
            'text-white text-xs px-3 py-2 rounded-lg shadow-lg max-w-[180px] transition-colors',
            canSnapClose ? 'bg-green-600' : 'bg-gray-800'
          )}
        >
          {canSnapClose ? (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Click để đóng!
            </span>
          ) : pointCount < 2 ? (
            <span>Click để vẽ điểm ({pointCount}/2)</span>
          ) : (
            <div className="space-y-1">
              <div className="font-medium">{pointCount} điểm</div>
              <div className="text-gray-300 text-[10px]">
                • Click điểm đầu → đóng thành vùng
                <br />• Enter/✓ → kết thúc đường
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
